import { ReactNode, useEffect, useState } from "react";
import { Button, Card, Flex, Text, toast } from "@jsq/ds/core";
import { Form, FormSelect, useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { ProgramType } from "~/graphql/supergraphEnums";
import { ChangeProgramsModal, ProgramChangeRow } from "./ChangeProgramsModal";
import { PROGRAM_OPTIONS, isNoProgram, resolveProgramLabel } from "./programOptions";
import * as drawerStyles from "./styles_vanilla.css";
import { SettingsDrawerTabProps } from "./types";

/** `errored` means the request failed and the caller's `onError` has already toasted. */
export type ApplyProgramOutcome = "saved" | "rejected" | "errored";

export type SingleProgramTabProps = SettingsDrawerTabProps & {
  subject: "entity" | "client";
  /** Card heading, and the row label in the Current | New diff. */
  programLabel: string;
  description: ReactNode;
  assignedProgram: string | null | undefined;
  successToast: string;
  applyProgram: (programType: ProgramType) => Promise<ApplyProgramOutcome>;
};

type FormValues = { program: string };

type FormProps = SingleProgramTabProps & { initialValues: FormValues };

const SingleProgramForm = ({
  row,
  onDirtyChange,
  onSaved,
  onCancel,
  initialValues,
  subject,
  programLabel,
  description,
  successToast,
  applyProgram,
}: FormProps) => {
  const { values, dirty } = useFormContext<FormValues>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  // Report dirtiness so the shell can guard close with Exit-and-discard
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const changes: ProgramChangeRow[] = [];
  if (
    values.program !== initialValues.program &&
    !(isNoProgram(values.program) && isNoProgram(initialValues.program))
  ) {
    changes.push({
      label: programLabel,
      current: initialValues.program || "--",
      next: values.program,
    });
  }

  const onApply = async () => {
    const selected = PROGRAM_OPTIONS.find((option) => option.label === values.program);
    if (!selected) return;
    setApplying(true);
    try {
      const outcome = await applyProgram(selected.programType);
      if (outcome === "saved") {
        setShowConfirm(false);
        toast.success(successToast);
        onSaved();
        return;
      }
      // The modal stays open so the user can retry or cancel
      if (outcome === "rejected") {
        toast.error(`Failed to update the ${subject} program for ${row.name}`);
      }
    } finally {
      setApplying(false);
    }
  };

  return (
    <Flex flexDirection="column" gap={Sizes.LG}>
      <Card isFullWidth>
        <Flex flexDirection="column" gap={Sizes.MD}>
          <Text variant="upper-md">{programLabel}</Text>
          <Text>{description}</Text>
          <div className={drawerStyles.programSelect}>
            <FormSelect
              name="program"
              label="Compliance program"
              placeholder="Select a compliance program"
              full
            >
              <FormSelect.Section>
                {PROGRAM_OPTIONS.map((option) => (
                  <FormSelect.Option key={option.label} value={option.label}>
                    {option.label}
                  </FormSelect.Option>
                ))}
              </FormSelect.Section>
            </FormSelect>
          </div>
        </Flex>
      </Card>

      <Flex justifyContent="flex-end" gap={10} className={drawerStyles.footerActions}>
        <Button variant="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => setShowConfirm(true)}
          disabled={!dirty || changes.length === 0}
          data-testid={`${subject}-program-save-button`}
        >
          Save
        </Button>
      </Flex>

      <ChangeProgramsModal
        show={showConfirm}
        title={`Change ${subject} programs`}
        description={
          <>
            {`Review the changes to the ${subject} programs for `}
            <Text asSpan variant="strong-md">
              {row.name}
            </Text>
            :
          </>
        }
        changes={changes}
        applying={applying}
        onCancel={() => setShowConfirm(false)}
        onApply={onApply}
      />
    </Flex>
  );
};

/**
 * The single-dropdown program tab shared by the Entity and Client panels. The Investor
 * tab is deliberately not built on this — it carries toggles and Legacy KYC handling.
 */
export const SingleProgramTab = (props: SingleProgramTabProps) => {
  const initialValues: FormValues = { program: resolveProgramLabel(props.assignedProgram) };

  return (
    <Form<FormValues>
      key={props.row.id}
      initialValues={initialValues}
      onSubmit={() => {
        // Saves run through the confirmation modal's Apply, not Formik submit
      }}
    >
      <SingleProgramForm {...props} initialValues={initialValues} />
    </Form>
  );
};
